import React, { useMemo, useRef, useState } from 'react';
import { useAppDispatch } from '../../../app/hooks';
import { performUserSelectionAction, toggleSingleItemSelection } from '../../../data/analyticsDataSlice';
import { getDataTable } from '../charts/chartDataBindings';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './Widget.module.scss';
import WidgetMenu from './WidgetMenu';
import WidgetDocViewer from '../widget-doc-viewer/WidgetDocViewer';
import { TpDataContext } from '../../../data/interfaces';
import { TpChartManifestConfigParamDef } from '../charts/interfaces';
import { BlockingOpenedItem } from '../../../components/blocking-opened-item/BlockingOpenedItem';
import {
  TpChartDefinition,
  TpDashboardContext,
  TpDashboardElement,
  TpWidgetContext,
  compareChartDefinitions,
  compareDashboardContexts,
} from '../dashboard/interfaces';
import { chartFactory } from '../charts/factory';
import { combineStyles } from '../helpers/misc';
import {
  removeElement,
  updateChartCompoundDataBinding,
  updateChartConfigParam,
  updateChartDataBinding,
} from '../data/dashboardsSlice';
import { ElementEditTools } from '../dashboard/edit-mode/ElementEditTools';
import { TpUserEnSelectionAction } from '../../../data/selection/userSelectionActions';
import validateChartDef from './validateChartDef';
import { DashboardElementErrorBoundary } from '../dashboard/DashboardElementErrorBoundary';
import { sendEvent } from '../hooks/events';
import { DASHBOARD_ACTION_TRIGGERIDS } from '../actions/executeActionTriggers';

interface TpWidgetProps {
  parentElemDef: TpDashboardElement | null;
  dataContext: TpDataContext;
  dashboardContext: TpDashboardContext;
  widgetDef: TpChartDefinition;
  handleOpenItem: (canonicalTypeId: string, itemId: string) => void;
}

interface TpWidgetBodyProps {
  dataContext: TpDataContext;
  dashboardContext: TpDashboardContext;
  widgetDef: TpChartDefinition;
  handleOpenItem: (canonicalTypeId: string, itemId: string) => void;
  zoomingActive: boolean;
  downloadFunctionRef: React.MutableRefObject<any>;
  menuActive: boolean;
  menuExpanded: boolean;
  inactivateMenu: () => void;
  magnified: boolean;
  setMagnified: (magnified: boolean) => void;
  docsActive: boolean;
  setDocsActive: (docsActive: boolean) => void;
}

function compareWidgetProps(prevProps: TpWidgetProps, nextProps: TpWidgetProps) {
  if (!compareDashboardContexts(prevProps.dashboardContext, nextProps.dashboardContext)) return false;
  if (!compareChartDefinitions(prevProps.widgetDef, nextProps.widgetDef)) return false;
  if (nextProps.dashboardContext.editMode) return false; //we don't try to cache when in edit mode
  return true;
}

function getCurrentDataBinding(definition: TpChartDefinition) {
  return definition.dataBinding;
}

function getCurrentConfigParams(chartTypeConfigParams: TpChartManifestConfigParamDef[], definition: TpChartDefinition) {
  const defaults: { [id: string]: any } = {};
  for (const param of chartTypeConfigParams) {
    defaults[param.id] = param.paramType.defaultValue;
  }
  return {
    ...defaults,
    ...definition.config,
  };
}

function WidgetBody({
  dataContext,
  dashboardContext,
  widgetDef,
  handleOpenItem,
  zoomingActive,
  downloadFunctionRef,
  menuActive,
  menuExpanded,
  inactivateMenu,
  magnified,
  setMagnified,
  docsActive,
  setDocsActive,
}: TpWidgetBodyProps) {
  const dispatch = useAppDispatch();

  const ChartManifest = useMemo(() => chartFactory(widgetDef.chartType), [widgetDef.chartType]);

  validateChartDef(ChartManifest, widgetDef);
  //throw getUserError('Invalid chart definition');

  const currentDataBinding = useMemo(() => getCurrentDataBinding(widgetDef), [widgetDef]);
  const currentConfigParams = useMemo(
    () => getCurrentConfigParams(ChartManifest!.configParams, widgetDef),
    [ChartManifest!.configParams, widgetDef],
  );

  const dataTable = getDataTable(dataContext, widgetDef.dataBinding.dataTableId);
  const canonicalTypeId = dataTable.canonicalTypeId;

  function handleUserSelectionAction(userSelectionAction: TpUserEnSelectionAction) {
    dispatch(
      performUserSelectionAction({
        dataContextId: dataContext.id,
        canonicalTypeId: canonicalTypeId,
        userSelectionAction,
      }),
    );
    sendEvent(DASHBOARD_ACTION_TRIGGERIDS.ENTITY_SELECTION_MODIFIED_BY_USER, {
      dataContextId: dataContext.id,
      canonicalTypeId,
    });
  }

  function onToggleSingleItemSelection(itemId: string) {
    dispatch(toggleSingleItemSelection({ dataContextId: dataContext.id, canonicalTypeId: canonicalTypeId, itemId }));
    sendEvent(DASHBOARD_ACTION_TRIGGERIDS.ENTITY_SELECTION_MODIFIED_BY_USER, {
      dataContextId: dataContext.id,
      canonicalTypeId,
    });
  }

  function updateCustomBinding(aspectId: string, columnId: string, onSecondaryTable: boolean) {
    dispatch(
      updateChartDataBinding({
        dashboardDefId: dashboardContext.dashboardDefId,
        chartId: widgetDef.id,
        aspectId,
        columnId,
        onSecondaryTable,
      }),
    );
  }

  function updateCustomCompoundBinding(aspectId: string, columnIds: string[]) {
    dispatch(
      updateChartCompoundDataBinding({
        dashboardDefId: dashboardContext.dashboardDefId,
        chartId: widgetDef.id,
        aspectId,
        columnIds,
      }),
    );
  }

  function updateConfigParam(configParamId: string, value: any) {
    dispatch(
      updateChartConfigParam({
        dashboardDefId: dashboardContext.dashboardDefId,
        chartId: widgetDef.id,
        configParamId,
        value,
      }),
    );
  }

  const widgetContext: TpWidgetContext = {
    dashboardContext,
    widgetId: widgetDef.id,
    zoomingActive,
    handleUserSelectionAction,
    toggleSingleItemSelection: onToggleSingleItemSelection,
    setBitmapDownloadFunction: (downloadFunction: any) => {
      downloadFunctionRef.current = downloadFunction;
    },
    handleOpenItem,
  };

  let theChart = null;
  if (ChartManifest) {
    theChart = (
      <DashboardElementErrorBoundary resetKeys={[widgetDef, dashboardContext.dataContextStatusIndex]}>
        <ChartManifest.component
          chartTypeManifest={ChartManifest}
          dataContext={dataContext}
          dataBinding={currentDataBinding}
          config={currentConfigParams}
          widgetContext={widgetContext}
        />
      </DashboardElementErrorBoundary>
    );
  }

  let menuStyle = combineStyles([styles.menu, styles.menuInactive]);
  if (menuExpanded) menuStyle = styles.menu;

  const theMenuContent = (
    <>
      <button
        onClick={() => {
          downloadFunctionRef.current?.();
        }}
      >
        Download
      </button>
      <WidgetMenu
        chartTypeManifest={ChartManifest}
        dataContext={dataContext}
        definition={widgetDef}
        currentDataBinding={currentDataBinding}
        currentConfigParams={currentConfigParams}
        updateCustomBinding={updateCustomBinding}
        updateCustomCompoundBinding={updateCustomCompoundBinding}
        updateConfigParam={updateConfigParam}
      />
    </>
  );

  return (
    <>
      {magnified && (
        <BlockingOpenedItem
          fraction={80}
          close={() => {
            setMagnified(false);
          }}
        >
          <div style={{ width: 'calc(100% - 260px)', height: '100%' }}>
            {theChart}
            <div className={styles.menu}>{theMenuContent}</div>
          </div>
        </BlockingOpenedItem>
      )}

      {docsActive && (
        <BlockingOpenedItem
          fraction={80}
          close={() => {
            setDocsActive(false);
          }}
        >
          <WidgetDocViewer
            chartTypeManifest={ChartManifest}
            dataContext={dataContext}
            definition={widgetDef}
            currentDataBinding={currentDataBinding}
            currentConfigParams={currentConfigParams}
          />
        </BlockingOpenedItem>
      )}

      {theChart}

      {menuActive && (
        <div className={styles.blockingOverlay} onClick={inactivateMenu}>
          <div className={menuStyle} onClick={(ev) => ev.stopPropagation()}>
            {theMenuContent}
          </div>
        </div>
      )}
    </>
  );
}

function Widget({ dataContext, dashboardContext, parentElemDef, widgetDef, handleOpenItem }: TpWidgetProps) {
  const refWidgetContainer = useRef<HTMLDivElement>(null);
  const refWidgetChartWrapper = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const downloadFunctionRef = useRef<any>(null);

  const [menuActive, setMenuActive] = useState(false);
  const [menuExpanded, setMenuExpanded] = useState(false);
  const [zoomingActive, setZoomingActive] = useState(false);

  const [magnified, setMagnified] = useState(false);
  const [docsActive, setDocsActive] = useState(false);
  const [hasStructuralError, setHasStructuralError] = useState(false);

  function removeThisWidget() {
    dispatch(removeElement({ dashboardDefId: dashboardContext.dashboardDefId, id: widgetDef.id }));
  }

  function activateMenu() {
    setMenuActive(true);
    setTimeout(() => {
      setMenuExpanded(true);
    }, 20);
  }

  function inactivateMenu() {
    setMenuExpanded(false);
    setTimeout(() => {
      setMenuActive(false);
    }, 250);
  }

  const style: any = {};
  if (widgetDef.elemLayoutHints?.sizeYAbsolute) {
    const widgetHeight = widgetDef.elemLayoutHints!.sizeYAbsolute - dashboardContext.elementVerticalSizeOffset;
    style.height = `${widgetHeight}px`;
  } else {
    style.height = '100%';
  }

  const userCanRemove = widgetDef.config?.userCanRemove;
  const informationAvailable = widgetDef.config?.customDescription != null;

  return (
    <div className={styles.widget} ref={refWidgetContainer} style={style} id={`widgetWrapper_${widgetDef.id}`}>
      {!menuActive && informationAvailable && (
        <button className={styles.informationButton} onClick={() => setDocsActive(true)}>
          <FontAwesomeIcon icon={['fad', 'info-circle']} />
        </button>
      )}
      {!menuActive && (
        <div className={styles.toolBox}>
          {!hasStructuralError && (
            <>
              <button
                className={zoomingActive ? styles.toolActive : styles.toolInactive}
                onClick={() => {
                  setZoomingActive(!zoomingActive);
                }}
              >
                <FontAwesomeIcon icon="magnifying-glass" />
              </button>
              <button className={styles.toolInactive} onClick={activateMenu}>
                <FontAwesomeIcon icon="gear" />
              </button>
              <button className={styles.toolInactive} onClick={() => downloadFunctionRef.current?.()}>
                <FontAwesomeIcon icon="download" />
              </button>
              <button className={styles.toolInactive} onClick={() => setMagnified(true)}>
                <FontAwesomeIcon icon="arrow-up-right-from-square" />
              </button>
            </>
          )}
          {userCanRemove && (
            <button className={styles.toolInactive} onClick={() => removeThisWidget()}>
              <FontAwesomeIcon icon="xmark" />
            </button>
          )}
        </div>
      )}

      <div
        className={!userCanRemove ? styles.widgetChartWrapper : styles.widgetChartWrapperCanRemove}
        ref={refWidgetChartWrapper}
      >
        <DashboardElementErrorBoundary
          resetKeys={[widgetDef, dashboardContext.dataContextStatusIndex]}
          onError={() => setHasStructuralError(true)}
          onReset={() => setHasStructuralError(false)}
        >
          <WidgetBody
            dataContext={dataContext}
            dashboardContext={dashboardContext}
            widgetDef={widgetDef}
            handleOpenItem={handleOpenItem}
            zoomingActive={zoomingActive}
            downloadFunctionRef={downloadFunctionRef}
            menuActive={menuActive}
            menuExpanded={menuExpanded}
            inactivateMenu={inactivateMenu}
            magnified={magnified}
            setMagnified={setMagnified}
            docsActive={docsActive}
            setDocsActive={setDocsActive}
          />
        </DashboardElementErrorBoundary>

        {dashboardContext.editMode && (
          <>
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(255,255,255,0.25)',
              }}
            />
            <ElementEditTools
              dataContext={dataContext}
              dashboardContext={dashboardContext}
              elemDef={widgetDef}
              parentElemDef={parentElemDef}
              dispatch={dispatch}
            />
          </>
        )}
      </div>

      {zoomingActive && <div className={styles.footerText}>Scroll to zoom, drag to pan</div>}
    </div>
  );
}

export default React.memo(Widget, compareWidgetProps);
